import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { session } from './session';
import type { ApiEnvelope, ApiErrorBody, Paged, PlanLimitDetails } from './types';

/**
 * The single HTTP client for the platform.
 *
 * Behaviour is ported from the battle-tested `web/js/api-client.js`:
 *   • every request carries the tenant header,
 *   • a 401 triggers ONE single-flight refresh, then the request is retried,
 *   • a 402 (plan limit) is announced platform-wide so any caller's limit hit
 *     can surface an upgrade prompt, wherever it originated,
 *   • responses are unwrapped from the server's `{ success, data, meta }`
 *     envelope so callers deal in domain objects, not transport shapes.
 */

/** Tenant header name — must match the API's TENANT_HEADER config. */
const TENANT_HEADER = 'x-tenant-id';

/** Thrown for every non-2xx response, carrying the server's error contract. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** The tenant's plan blocks this action (server: PaymentRequiredError). */
  get isPlanLimit(): boolean {
    return this.status === 402;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
  /** Server-side validation failure; `details` holds the field errors. */
  get isValidation(): boolean {
    return this.status === 422 || this.code === 'VALIDATION_ERROR';
  }
}

/** Fired on any 402 so the UI can offer an upgrade regardless of call site. */
export const PLAN_LIMIT_EVENT = 'lp:plan-limit';

export interface PlanLimitEventDetail {
  message: string;
  details?: PlanLimitDetails;
}

/** Fired when refresh fails and the session is dead — the app must sign out. */
export const SESSION_EXPIRED_EVENT = 'lp:session-expired';

function emit(name: string, detail: unknown): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }
}

/** Requests we've already retried once, so a refresh loop is impossible. */
type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

export const http: AxiosInstance = axios.create({
  baseURL: '/',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const tenant = session.tenant;
  if (tenant) config.headers.set(TENANT_HEADER, tenant);

  // `authRequired: false` opts a call out of the bearer token — used by the
  // public white-label branding endpoint, which must never send credentials.
  const authRequired = (config as AxiosRequestConfig & { authRequired?: boolean }).authRequired;
  const token = session.accessToken;
  if (authRequired !== false && token) config.headers.set('Authorization', `Bearer ${token}`);

  return config;
});

/**
 * Single-flight refresh: N concurrent 401s trigger ONE refresh call and all
 * await the same promise, rather than stampeding the token endpoint and
 * invalidating each other's rotated refresh token.
 */
let refreshing: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = session.refreshToken;
  if (!refreshToken) throw new Error('no refresh token');

  if (!refreshing) {
    refreshing = axios
      // Bare axios, not `http` — this must never recurse through the 401 interceptor.
      .post<ApiEnvelope<{ accessToken: string; refreshToken: string }>>(
        '/api/v1/auth/refresh',
        { refreshToken },
      )
      .then((res) => {
        const data = res.data.data;
        session.save(data);
        return data.accessToken;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

function toApiError(error: AxiosError<ApiErrorBody>): ApiError {
  const body = error.response?.data;
  const status = error.response?.status ?? 0;
  const message =
    body?.error?.message ??
    (status === 0 ? 'Cannot reach the server. Check your connection.' : `Request failed (${status})`);
  return new ApiError(message, status, body?.error?.code ?? 'NETWORK_ERROR', body?.error?.details);
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetryableConfig | undefined;
    const status = error.response?.status;

    // ── 401: refresh once, then retry the original request ──
    if (status === 401 && config && !config._retried && session.refreshToken) {
      config._retried = true;
      try {
        await refreshAccessToken();
        return http(config);
      } catch {
        session.clear();
        emit(SESSION_EXPIRED_EVENT, null);
      }
    }

    const apiError = toApiError(error);

    // ── 402: a plan limit is a product event, not just a failed call ──
    if (apiError.isPlanLimit) {
      emit(PLAN_LIMIT_EVENT, {
        message: apiError.message,
        details: apiError.details as PlanLimitDetails | undefined,
      } satisfies PlanLimitEventDetail);
    }

    return Promise.reject(apiError);
  },
);

/** Request options; `authRequired: false` sends no bearer token. */
export type RequestOptions = AxiosRequestConfig & { authRequired?: boolean };

/** Unwraps the envelope, returning `data`. */
export async function request<T>(config: RequestOptions): Promise<T> {
  const res = await http.request<ApiEnvelope<T>>(config);
  return res.data.data;
}

/** Unwraps a paginated list, returning both `items` and pagination `meta`. */
export async function requestPaged<T>(config: RequestOptions): Promise<Paged<T>> {
  const res = await http.request<ApiEnvelope<T[]>>(config);
  return { items: res.data.data ?? [], meta: res.data.meta?.pagination };
}

export const api = {
  get: <T>(url: string, params?: unknown, options?: RequestOptions) =>
    request<T>({ ...options, method: 'GET', url, params }),

  list: <T>(url: string, params?: unknown, options?: RequestOptions) =>
    requestPaged<T>({ ...options, method: 'GET', url, params }),

  post: <T>(url: string, data?: unknown, options?: RequestOptions) =>
    request<T>({ ...options, method: 'POST', url, data }),

  patch: <T>(url: string, data?: unknown, options?: RequestOptions) =>
    request<T>({ ...options, method: 'PATCH', url, data }),

  put: <T>(url: string, data?: unknown, options?: RequestOptions) =>
    request<T>({ ...options, method: 'PUT', url, data }),

  delete: <T>(url: string, options?: RequestOptions) =>
    request<T>({ ...options, method: 'DELETE', url }),
};
